import { NumberInputType, EPricingTableColumn, PricingTableHelper, getRowDescriptionValue, getRowDiscountFlatFee, getRowDiscountValue, getRowDiscountValueIsEmpty, getRowFormattedDiscount, getRowNameValue, getRowNetPrice, getRowOptionalProductField, getRowPriceAfterDiscounts, getRowSkuValue, getRowTaxFlatFee, getRowTaxValue, getRowTaxValueIsEmpty, getRowTotalPrice, getRowValues, getRowVariableQuantityEnabled, getRowVariableQuantityField, getRowOptionalProductEnabled, getFilteredRowValues, getRowFormattedTax, getRowDescriptionIsEmpty, getRowSkuValueIsEmpty, getRowDiscountEnabled, getNumberInputType, hasDiscount, hasCustomColumn, Context, getTotalPriceEnabled, } from '@getaccept/editor-lib-new';
import { PricingTableCheckbox } from '@getaccept/editor-lib-new/src/components/pricing-table';
import debounce from 'debounce';
import { SigningHelper } from '@getaccept/lib-shared-new/src/signature/helpers/signing.helper';
import { onMounted, watch, computed, ref, defineComponent } from 'vue';
import PricingTableUnitsModifierMenu from './PricingTableUnitsModifierMenu.vue';
import PricingTableDescriptionInput from './PricingTableDescriptionInput.vue';
export default defineComponent({
    name: 'PricingTableRow',
    components: {
        PricingTableDescriptionInput,
        PricingTableCheckbox,
        PricingTableUnitsModifierMenu,
    },
    props: {
        row: { type: Object, default: () => ({}) },
        columns: { type: Array, default: () => [] },
        isBlockWidthSmall: { type: Boolean },
        fields: { type: Array, default: () => [] },
        recipientInput: { type: Array, default: () => [] },
        recipient: {
            type: Object,
            default: () => ({}),
        },
        context: { type: String, default: () => Context.Document },
        currencySettings: {
            type: Object,
            default: () => ({}),
        },
        gridTemplateColumns: { type: String, default: '' },
        mergeValues: { type: Object, default: () => ({}) },
        isPreCalculated: { type: Boolean },
        allowedToEditAfterPartiallySigned: { type: Boolean },
    },
    // eslint-disable-next-line sonarjs/no-duplicate-string
    emits: ['update-input-value'],
    setup(props, { emit }) {
        const rowVariableQuantityField = ref({
            value: null,
        });
        const showVariableQuantityMenu = ref(false);
        const showDescription = computed(() => !getRowDescriptionIsEmpty(props.columns, getRowValues(props.row)));
        const showSku = computed(() => !getRowSkuValueIsEmpty(props.columns, getRowValues(props.row)));
        const showTotalPrice = computed(() => getTotalPriceEnabled(props.columns));
        const showDiscount = computed(() => getRowDiscountEnabled(props.columns) &&
            !getRowDiscountValueIsEmpty(props.columns, getRowValues(props.row)));
        const isDocumentView = computed(() => props.context === Context.DocumentView);
        const recipientStatus = computed(() => { var _a; return (_a = props.recipient) === null || _a === void 0 ? void 0 : _a.status; });
        const isEditable = computed(() => (props.allowedToEditAfterPartiallySigned &&
            !SigningHelper.hasRecipientSigned(recipientStatus.value) &&
            !isDocumentView.value) ||
            props.context === Context.DsrPublished);
        const isVariableQuantityEditable = computed(() => (!SigningHelper.hasRecipientSigned(recipientStatus.value) &&
            props.allowedToEditAfterPartiallySigned) ||
            props.context === Context.DsrPublished);
        const hasOptionalProductDeselected = computed(() => { var _a; return ((_a = rowOptionalProductInputField()) === null || _a === void 0 ? void 0 : _a.value) === '0'; });
        const rowOptionalProductInputField = () => {
            let field = getRowOptionalProductField(props.row, props.fields);
            if (!field) {
                return null;
            }
            field = Object.assign(Object.assign({}, field), { value: getFieldValue(field) });
            return field;
        };
        const rowVariableQuantityInputField = () => {
            let field = getRowVariableQuantityField(props.row, props.fields);
            if (!field) {
                return null;
            }
            field = Object.assign(Object.assign({}, field), { value: getFieldValue(field) });
            return field;
        };
        const getFieldValue = (input) => {
            var _a, _b;
            if (!input) {
                return;
            }
            const fieldValue = (_b = (_a = props.recipientInput) === null || _a === void 0 ? void 0 : _a.find(field => field.fieldId === input.id)) === null || _b === void 0 ? void 0 : _b.value;
            if (fieldValue) {
                return fieldValue;
            }
            return input.value;
        };
        const handleDebounceUpdateVariableQuantityField = (payload) => {
            emit('update-input-value', payload);
        };
        const debounceUpdate = debounce(handleDebounceUpdateVariableQuantityField, 300);
        const handleUpdateVariableQuantityField = (payload) => {
            rowVariableQuantityField.value.value = payload.value;
            debounceUpdate(payload);
        };
        const handleUpdateChecked = (payload) => {
            emit('update-input-value', payload);
        };
        const handleCloseVariableQuantityMenu = () => {
            showVariableQuantityMenu.value = false;
        };
        const getFormattedPrice = (price) => {
            const value = price === '' ? '0' : price;
            return PricingTableHelper.currencyFormat(value, props.currencySettings);
        };
        const getFormat = (rowValue) => {
            const isCustom = hasCustomColumn(props.columns, rowValue.columnId);
            if (isCustom) {
                return rowValue.value || '-';
            }
            const type = getNumberInputType(props.columns, rowValue.columnId);
            const value = rowValue.value || '0';
            switch (type) {
                case NumberInputType.Percentage:
                    return `${value}%`;
                case NumberInputType.Currency:
                    return PricingTableHelper.currencyFormat(value, props.currencySettings);
                case NumberInputType.Number:
                case NumberInputType.Text:
                default:
                    return value;
            }
        };
        onMounted(() => {
            rowVariableQuantityField.value = rowVariableQuantityInputField();
        });
        watch(() => [props.fields, props.recipientInput], () => {
            rowVariableQuantityField.value = rowVariableQuantityInputField();
        });
        return {
            rowVariableQuantityField,
            showVariableQuantityMenu,
            hasRecipientSigned: SigningHelper.hasRecipientSigned,
            getColumnDisplayName: PricingTableHelper.getColumnDisplayName,
            getColumnIdFromName: PricingTableHelper.getColumnIdFromName,
            currencyFormat: PricingTableHelper.currencyFormat,
            debounceUpdate,
            showDescription,
            showSku,
            showDiscount,
            isDocumentView,
            recipientStatus,
            isEditable,
            hasOptionalProductDeselected,
            rowOptionalProductInputField,
            rowVariableQuantityInputField,
            getFieldValue,
            handleUpdateVariableQuantityField,
            handleDebounceUpdateVariableQuantityField,
            handleUpdateChecked,
            handleCloseVariableQuantityMenu,
            getFormattedPrice,
            NumberInputType,
            getRowOptionalProductEnabled,
            getRowOptionalProductField,
            getRowDescriptionValue,
            getRowValues,
            EPricingTableColumn,
            getRowSkuValue,
            getFilteredRowValues,
            getRowVariableQuantityEnabled,
            isVariableQuantityEditable,
            getRowTaxValue,
            getRowTaxValueIsEmpty,
            getRowTaxFlatFee,
            getRowDiscountValueIsEmpty,
            getRowPriceAfterDiscounts,
            getRowTotalPrice,
            getRowFormattedDiscount,
            getRowFormattedTax,
            getRowNetPrice,
            getRowNameValue,
            getNumberInputType,
            getRowDiscountValue,
            getRowDiscountFlatFee,
            getRowVariableQuantityField,
            hasDiscount,
            getFormat,
            hasCustomColumn,
            showTotalPrice,
        };
    },
});
